import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Body2, Caption1, CloseSmallIcon, ConfirmDialog, Dot, IconButton, Tooltip } from "@activecollab/components";
import PropTypes from "prop-types";
import { EntityLink } from "~/components/EntityLink";
import AvatarContainer from "~/components/Users/AvatarContainer";
import { invalidateTemplateDependenciesCount } from "~/repository";
import { DELETE_PROJECT_TEMPLATE_TASK_DEPENDENCIES_SUCCESS } from "~/store/action_types";
import { deleteProjectTemplateTasksDependency } from "~/store/actions/actions.projectTemplateTaskDependencies";
import { getProjectTemplateElementById } from "~/store/selectors/selector.projectTemplateElements";
import { getUserById } from "~/store/selectors/selector.users";
import { resolveDueOnTooltipText, resolveStartAndDueOn } from "../../utils";

const TemplateTaskDependenciesItem = ({ id, parentId, isParent }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [dependencyTaskId, setDependencyTaskId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const task = useSelector((state) => getProjectTemplateElementById(state, id));
  const user = useSelector((state) => getUserById(state, task.assignee_id))?.toJS();

  const viewUrl = useMemo(() => `/project-templates/${task?.template_id}/elements/${id}`, [id, task?.template_id]);
  const hashUrl = useMemo(() => `TemplateTask-${task?.id}-${task?.template_id}`, [task?.id, task?.template_id]);
  const dueOn = useMemo(() => task?.start_on || task?.due_on, [task?.due_on, task?.start_on]);
  const resolvedStartAndDueOn = useMemo(() => resolveStartAndDueOn(task), [task]);
  const dueOnTooltipText = useMemo(() => resolveDueOnTooltipText(task), [task]);

  const onCancel = useCallback(() => {
    setDependencyTaskId(null);
  }, []);

  const handleDeleteDependency = useCallback(() => {
    setIsSubmitting(true);
    dispatch(deleteProjectTemplateTasksDependency(parentId, dependencyTaskId, isParent))
      .then((r) => {
        if (r.type === DELETE_PROJECT_TEMPLATE_TASK_DEPENDENCIES_SUCCESS) {
          invalidateTemplateDependenciesCount(task?.template_id);
          onCancel();
        }
      })
      .finally(() => setIsSubmitting(false));
  }, [dependencyTaskId, dispatch, isParent, onCancel, parentId, task?.template_id]);

  return (
    <>
      <div className="tw-group group-hover:tw-bg-theme-200 hover:tw-bg-theme-200 tw-grid tw-grid-cols-[1fr_auto] tw-items-center tw-w-full tw-h-8 tw-rounded-lg -tw-ml-1.5 tw-px-1.5 tw-gap-2">
        <div className="tw-flex tw-items-center tw-min-w-0">
          <EntityLink
            to={viewUrl}
            className="tw-no-underline tw-overflow-hidden"
            hashUrl={hashUrl}
          >
            <Body2
              className="tw-min-w-0"
              whitespace="no-wrap"
              overflow="truncate"
              color={task.is_important ? "alert" : "primary"}
            >
              {task.name}
            </Body2>
          </EntityLink>
          {!!dueOn && (
            <div className="tw-flex tw-items-center tw-shrink-0">
              <Dot
                size={2}
                color="var(--color-theme-600)"
                className="tw-shrink-0 tw-mx-2 tw-align-middle"
              />
              <Tooltip title={dueOnTooltipText}>
                <Caption1>{resolvedStartAndDueOn}</Caption1>
              </Tooltip>
            </div>
          )}
        </div>
        <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
          {task.assignee_id ? (
            <Tooltip title={user?.display_name}>
              <AvatarContainer
                id={task.assignee_id}
                className="tw-ml-2"
              />
            </Tooltip>
          ) : null}
          <div className="tw-opacity-0 group-hover:tw-opacity-100">
            <Tooltip title={t("Remove dependency")}>
              <IconButton
                size="small"
                variant="text gray"
                onClick={() => setDependencyTaskId(id)}
              >
                <CloseSmallIcon />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      </div>
      <ConfirmDialog
        open={!!dependencyTaskId}
        onCancel={onCancel}
        onConfirm={handleDeleteDependency}
        dialogTitle={t("Delete Dependency")}
        dialogContent={t("Are you sure that you want to delete this dependency?")}
        confirmBtnText={t("Delete")}
        cancelBtnText={t("Cancel")}
        isLoading={isSubmitting}
      />
    </>
  );
};

TemplateTaskDependenciesItem.propTypes = {
  id: PropTypes.number.isRequired,
  parentId: PropTypes.number.isRequired,
  isParent: PropTypes.bool,
};

export default TemplateTaskDependenciesItem;
